const MESSAGE_IDS = {
  connectivity: 'app.toast.error.connectionIssue',
  timeout: 'app.toast.error.requestTimedOut',
  serviceUnavailable: 'app.toast.error.serviceUnavailable',
};

const DEFAULT_MESSAGES = {
  [MESSAGE_IDS.connectivity]:
    'Your internet connection appears to be offline. Check your connection and try again.',
  [MESSAGE_IDS.timeout]:
    'The request took too long to complete. Please try again.',
  [MESSAGE_IDS.serviceUnavailable]:
    "We couldn't complete the request because the service is temporarily unavailable or returned an error. Please try again.",
};

// Priority Passport rejections reach the Portal as "Error: (BL-0519) <resident-facing text>".
// Only its BL/VA codes qualify, and the text can't hold a control character or line separator,
// so a stack trace or another service's coded error never reaches the resident.
const PROVIDER_ERROR_PATTERN =
  /^(?:Error: ?)*(\((?:BL|VA)-\d{4}\) [^\p{Cc}\u2028\u2029]{1,300})$/u;

const getProviderMessage = (rawMessage) => {
  const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';

  return message.match(PROVIDER_ERROR_PATTERN)?.[1];
};

const TRANSPORT_ERROR_PATTERN =
  /failed to fetch|network error|load failed|network request failed|internet connection appears to be offline/i;

const getNavigatorOnlineStatus = () => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.onLine;
};

export const extractAffordaPortalErrorMessage = (error) => {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.response?.data === 'string') {
    return error.response.data;
  }

  if (typeof error?.response?.data?.message === 'string') {
    return error.response.data.message;
  }

  if (typeof error?.response?.data?.errorMessage === 'string') {
    return error.response.data.errorMessage;
  }

  if (typeof error?.response?.data?.error === 'string') {
    return error.response.data.error;
  }

  if (typeof error?.response?.data?.error?.message === 'string') {
    return error.response.data.error.message;
  }

  if (typeof error?.networkError?.message === 'string') {
    return error.networkError.message;
  }

  if (Array.isArray(error?.graphQLErrors)) {
    const firstMessage = error.graphQLErrors.find(
      ({ message }) => typeof message === 'string' && message.length,
    )?.message;

    if (firstMessage) {
      return firstMessage;
    }
  }

  if (typeof error?.error === 'string') {
    return error.error;
  }

  if (typeof error?.error?.message === 'string') {
    return error.error.message;
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return '';
};

const getStatusCode = (error) =>
  error?.statusCode ??
  error?.status ??
  error?.response?.status ??
  error?.networkError?.statusCode ??
  error?.error?.statusCode;

const getErrorCode = (error) =>
  error?.code ??
  error?.networkError?.code ??
  error?.error?.code ??
  error?.classification?.errorCode;

const hasGraphQlErrors = (error) =>
  Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0;

const hasResponse = (error, statusCode) =>
  Boolean(error?.response) ||
  Boolean(error?.networkError?.result) ||
  Boolean(error?.classification?.hasResponse) ||
  Number.isInteger(statusCode);

const getClassificationMessage = (classification, fallbackMessage) => {
  const shouldPreserveRawMessage =
    typeof classification?.rawMessage === 'string' &&
    classification.rawMessage.length > 0 &&
    classification.category === 'api' &&
    classification.statusCode >= 400 &&
    classification.statusCode < 500 &&
    classification.reason !== 'timeout';

  if (shouldPreserveRawMessage) {
    return classification.rawMessage;
  }

  if (classification?.providerMessage) {
    return classification.providerMessage;
  }

  if (classification?.userMessageId) {
    return DEFAULT_MESSAGES[classification.userMessageId];
  }

  return classification?.rawMessage || fallbackMessage || 'Request failed.';
};

const getDerivedUserMessageId = ({ category, reason }) => {
  if (category === 'connectivity') {
    return MESSAGE_IDS.connectivity;
  }

  if (reason === 'timeout') {
    return MESSAGE_IDS.timeout;
  }

  if (
    category === 'api' &&
    ['server', 'transport', 'graphql'].includes(reason)
  ) {
    return MESSAGE_IDS.serviceUnavailable;
  }

  return undefined;
};

const applyDerivedClassification = (classification, overrides = {}) => {
  const nextClassification = {
    ...classification,
  };
  const hasExplicitUserMessageId = Object.prototype.hasOwnProperty.call(
    overrides,
    'userMessageId',
  );
  const hasExplicitMessage = Object.prototype.hasOwnProperty.call(
    overrides,
    'message',
  );

  nextClassification.isApiFailure = nextClassification.category === 'api';
  nextClassification.isConnectivityIssue =
    nextClassification.category === 'connectivity';
  nextClassification.isOffline =
    nextClassification.reason === 'offline' ||
    nextClassification.category === 'connectivity';
  nextClassification.isTimeout = nextClassification.reason === 'timeout';
  nextClassification.providerMessage =
    nextClassification.reason === 'graphql'
      ? getProviderMessage(nextClassification.rawMessage)
      : undefined;

  if (!hasExplicitUserMessageId) {
    nextClassification.userMessageId =
      getDerivedUserMessageId(nextClassification);
  }

  nextClassification.message = hasExplicitMessage
    ? overrides.message
    : getClassificationMessage(
        nextClassification,
        nextClassification.rawMessage || 'Request failed.',
      );

  return nextClassification;
};

export const classifyAffordaPortalError = (error) => {
  const onlineStatus = getNavigatorOnlineStatus();
  const rawMessage =
    error?.affordaPortal?.rawMessage ||
    error?.classification?.rawMessage ||
    extractAffordaPortalErrorMessage(error);
  const statusCode = getStatusCode(error);
  const errorCode = getErrorCode(error);
  const responsePresent = hasResponse(error, statusCode);
  const timeout =
    errorCode === 'ECONNABORTED' || /timeout/i.test(rawMessage || '');
  const offline = onlineStatus === false;
  const transportFailure =
    errorCode === 'ERR_NETWORK' ||
    TRANSPORT_ERROR_PATTERN.test(rawMessage || '') ||
    (Boolean(error?.request) && !error?.response) ||
    (Boolean(error?.networkError) && !responsePresent);
  const graphQlError = hasGraphQlErrors(error);

  let category = 'unknown';
  let reason = 'unknown';

  if (offline) {
    category = 'connectivity';
    reason = 'offline';
  } else if (timeout) {
    category = 'api';
    reason = 'timeout';
  } else if (statusCode >= 400) {
    category = 'api';
    reason = statusCode >= 500 ? 'server' : 'response';
  } else if (graphQlError) {
    category = 'api';
    reason = 'graphql';
  } else if (transportFailure) {
    category = 'api';
    reason = 'transport';
  }

  return applyDerivedClassification({
    category,
    reason,
    errorCode,
    hasResponse: responsePresent,
    rawMessage,
    statusCode,
    userMessageId: getDerivedUserMessageId({ category, reason }),
    isApiFailure: category === 'api',
    isConnectivityIssue: category === 'connectivity',
    isOffline: offline,
    isTimeout: timeout,
  });
};

export const getAffordaPortalErrorMessageId = (error, fallbackMessageId) => {
  const { userMessageId } =
    error?.affordaPortal || classifyAffordaPortalError(error);

  return userMessageId || fallbackMessageId;
};

export const createAffordaPortalError = (error, overrides = {}) => {
  const classification = applyDerivedClassification(
    {
      ...classifyAffordaPortalError(error),
      ...overrides,
    },
    overrides,
  );
  const normalizedError = new Error(classification.message);

  if (error?.stack) {
    normalizedError.stack = error.stack;
  }

  normalizedError.name =
    classification.category === 'connectivity'
      ? 'AffordaPortalConnectivityError'
      : classification.reason === 'timeout'
        ? 'AffordaPortalTimeoutError'
        : classification.category === 'api'
          ? 'AffordaPortalApiError'
          : error?.name || 'Error';
  normalizedError.affordaPortal = classification;
  normalizedError.code = classification.errorCode;
  normalizedError.originalError = error;
  normalizedError.statusCode = classification.statusCode;

  return normalizedError;
};

export const toReportableError = (error) => {
  const reportableError = new Error(error?.message);
  reportableError.name = error?.name || 'Error';
  if (error?.stack) {
    reportableError.stack = error.stack;
  }

  return reportableError;
};
